
import { computed, defineComponent, onBeforeUnmount, ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faCamera,
  faCloudArrowUp,
  faPen,
  faSpinner,
  faUser,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import AvatarCropModal from '@/components/shared/avatar-uploader/AvatarCropModal.vue';
import { ProfileService } from '@/core/services/ProfileService';
import { resolveAvatarUrl } from '@/core/helpers/avatar';
import { swalErrNotification, swalNotification } from '@/core/helpers/utils';
import type { IMeProfile } from '@/core/interfaces/ApiResponses';

const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
];

export default defineComponent({
  name: 'avatar-uploader',

  components: {
    AvatarCropModal,
    FontAwesomeIcon,
  },

  props: {
    modelValue: {
      type: String,
      default: '',
    },
    editable: {
      type: Boolean,
      default: true,
    },
    alt: {
      type: String,
      default: 'Ảnh đại diện',
    },
    maxSizeMb: {
      type: Number,
      default: 2,
    },
  },

  emits: ['update:modelValue', 'uploaded', 'preview-change'],

  setup(props, { emit }) {
    const fileInputRef = ref<HTMLInputElement | null>(null);
    const uploading = ref(false);

    const isCropOpen = ref(false);
    const editorSrc = ref('');
    const sourceFileName = ref('avatar.jpg');
    const sourceMimeType = ref('image/jpeg');

    const pendingFile = ref<File | null>(null);
    const pendingPreviewUrl = ref('');

    const displaySrc = computed(
      () => pendingPreviewUrl.value || props.modelValue || ''
    );

    const revokeUrl = (url: string) => {
      if (url && url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    };

    const clearEditorSrc = () => {
      revokeUrl(editorSrc.value);
      editorSrc.value = '';
    };

    const clearPendingPreview = () => {
      revokeUrl(pendingPreviewUrl.value);
      pendingPreviewUrl.value = '';
      pendingFile.value = null;
    };

    const resetFileInput = () => {
      if (fileInputRef.value) {
        fileInputRef.value.value = '';
      }
    };

    const cancelPending = () => {
      clearPendingPreview();
      clearEditorSrc();
      resetFileInput();
      emit('preview-change', null);
    };

    const onCropCancel = () => {
      clearEditorSrc();
      resetFileInput();
    };

    const onFileChange = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) {
        return;
      }

      if (!ACCEPTED_TYPES.includes(file.type)) {
        swalNotification(
          'Ảnh đại diện chỉ hỗ trợ jpg, jpeg, png, gif, webp',
          'warning'
        );
        input.value = '';
        return;
      }

      const maxBytes = props.maxSizeMb * 1024 * 1024;
      if (file.size > maxBytes) {
        swalNotification(
          `Ảnh đại diện không được vượt quá ${props.maxSizeMb}MB`,
          'warning'
        );
        input.value = '';
        return;
      }

      clearEditorSrc();
      sourceFileName.value = file.name || 'avatar.jpg';
      sourceMimeType.value = file.type || 'image/jpeg';
      editorSrc.value = URL.createObjectURL(file);
      isCropOpen.value = true;
    };

    const onCropConfirm = (file: File) => {
      clearPendingPreview();
      pendingFile.value = file;
      pendingPreviewUrl.value = URL.createObjectURL(file);
      emit('preview-change', file);
      clearEditorSrc();
      resetFileInput();
    };

    const reopenEditor = () => {
      if (!pendingFile.value) {
        return;
      }
      clearEditorSrc();
      sourceFileName.value = pendingFile.value.name || 'avatar.jpg';
      sourceMimeType.value = pendingFile.value.type || 'image/jpeg';
      editorSrc.value = URL.createObjectURL(pendingFile.value);
      isCropOpen.value = true;
    };

    const upload = async () => {
      if (!pendingFile.value) {
        return;
      }

      try {
        uploading.value = true;
        const {
          data: { data: profile },
        } = await ProfileService.uploadAvatar(pendingFile.value);

        ProfileService.applyProfileToStore(profile);

        const avatarUrl = resolveAvatarUrl(
          profile.anh_dai_dien,
          profile.anh_dai_dien_url
        );
        emit('update:modelValue', avatarUrl);
        emit('uploaded', profile as IMeProfile);
        await swalNotification('Cập nhật ảnh đại diện thành công', 'success');

        clearPendingPreview();
        resetFileInput();
      } catch (error) {
        await swalErrNotification(
          error,
          'Không thể tải ảnh đại diện, vui lòng thử lại'
        );
      } finally {
        uploading.value = false;
      }
    };

    onBeforeUnmount(() => {
      clearPendingPreview();
      clearEditorSrc();
    });

    return {
      cancelPending,
      displaySrc,
      editorSrc,
      faCamera,
      faCloudArrowUp,
      faPen,
      faSpinner,
      faUser,
      faXmark,
      fileInputRef,
      isCropOpen,
      onCropCancel,
      onCropConfirm,
      onFileChange,
      pendingFile,
      reopenEditor,
      sourceFileName,
      sourceMimeType,
      upload,
      uploading,
    };
  },
});
